import React, { useEffect, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import DefaultDialog from '../../../components/Dialog/Default';
import { convertDriver } from '../../../repositories/HttpRepository';
import { CONVERSION_STATE, IN_PROGRESS_STAGES, getConvertButtonState } from '../../../utils/driverConversion';

const buttonStyle = {
  padding: '4px 8px',
  marginLeft: '5px',
  marginTop: '5px',
  boxShadow: 'none',
};

const disabledButtonSx = {
  ...buttonStyle,
  display: 'inline-flex',
  borderRadius: 1,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 'normal',
  verticalAlign: 'middle',
  color: 'action.disabled',
  backgroundColor: 'action.disabledBackground',
  cursor: 'not-allowed',
  userSelect: 'none',
};

/**
 * Convert button for one row of the DSP Drivers' List.
 * Greys out (with a hover reason) or hides itself from `driver.conversion`,
 * and on confirm calls the convert API and updates the row from its response.
 */
function ConvertDriverButton({ driver, onRefreshList }) {
  const [conversion, setConversion] = useState(driver.conversion);
  const [loading, setLoading] = useState(false);
  const { hidden, disabled, tooltip } = getConvertButtonState(conversion);

  useEffect(() => {
    // For convert failed by BE validation or conversion state changed
    // The state of button should change follow new conversion state
    setConversion(driver.conversion);
  }, [driver.conversion]);

  const handleAgreeConvert = () => {
    setLoading(true);
    convertDriver(driver.id)
      .then((res) => {
        const result = res?.data;
        const errorMessage = result?.message || 'Server has some problems';

        switch (res?.status) {
          case 204:
            // Old flow - Converted, driver is left the DSP and reset to a new applicant
            // and must redo onboarding
            toast.success("This driver has been converted to Jitsu's driver");
            onRefreshList();
            return;
          case 200: {
            // For pilot region (SFO,MCI)
            // Start conversion -> state to IN_PROGRESS; the body is the new conversion status,
            toast.success(
              `Conversion submitted successfully with status ${IN_PROGRESS_STAGES[result?.stage] || 'In progress'}`,
            );
            const nextState = result || {
              state: CONVERSION_STATE.IN_PROGRESS,
              stage: 'AWAITING_PROFILE',
            };
            setConversion(nextState);
            break;
          }
          case 409:
            toast.error(errorMessage);
            if (result?.code === 'BLOCKED_BY_FAILED_CHECK') {
              // Convert refused by a failed background check: block the row
              setConversion({
                state: CONVERSION_STATE.BLOCKED,
                blocked_reason: result.blocked_reason,
                blocked_until: result.blocked_until,
              });
            } else {
              // ALREADY_IN_PROGRESS / LOST_RACE mean it's converting:
              setConversion({ state: CONVERSION_STATE.IN_PROGRESS, lastMessage: result?.message });
            }
            break;
          case 412:
            // BACKGROUND_CHECK_IN_FLIGHT / NOT_LINKED_TO_COURIER
            toast.error(errorMessage);
            setConversion({ state: CONVERSION_STATE.IN_PROGRESS, lastMessage: result?.message });
            break;
          default:
            // Could be server error here
            toast.error(errorMessage);
            break;
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (hidden) {
    return null;
  }

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <Typography component="span" role="button" aria-disabled="true" sx={disabledButtonSx} tabIndex={0}>
        Convert
      </Typography>
    </Tooltip>
  ) : (
    <DefaultDialog
      btnString="Convert"
      title="Do you want to convert this driver to Jitsu's driver?"
      description=""
      handleAgree={handleAgreeConvert}
      disabled={disabled || loading}
      isLoading={loading}
      style={buttonStyle}
    />
  );
}

ConvertDriverButton.propTypes = {
  driver: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    conversion: PropTypes.shape({
      state: PropTypes.string,
      stage: PropTypes.string,
      blocked_reason: PropTypes.string,
      blocked_until: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      started_ts: PropTypes.number,
      converted_ts: PropTypes.number,
    }),
  }).isRequired,
  onRefreshList: PropTypes.func.isRequired,
};

export default React.memo(ConvertDriverButton);
